import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Card, CardBody, Container, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import * as demTemplatesActions from '../../store/demTemplates/actions';
import {
  selectDemTemplates,
  selectDemTemplatesLoading,
} from '../../selectors/demTemplate';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DataTableNext from '../../components/Common/DataTable/DataTableNext';
import createTableColumns from '../../components/DemTemplates/tableColumns';
import {
  CountRowTablePlacement,
  CreateButton,
  ToggleColumnButton,
  ExportButtonPlacement,
} from '../../components/Common/DataTable/tableActionButtons';
import { addOrRemove } from '../../helpers/sharedFunction';

const List = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const demTemplates = useSelector(selectDemTemplates);
  const loading = useSelector(selectDemTemplatesLoading);

  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    dispatch(demTemplatesActions.syncDemTemplates());
    return () => dispatch(demTemplatesActions.resetState());
  }, [dispatch]);

  const handleEdit = useCallback(
    (template) => {
      history.push(`/demTemplates/edit/${template.id}`);
    },
    [history],
  );

  const handleDuplicate = useCallback(
    (template) => {
      dispatch(demTemplatesActions.duplicateDemTemplate(template.id));
    },
    [dispatch],
  );

  const columns = useMemo(
    () => createTableColumns(handleEdit, handleDuplicate),
    [handleEdit, handleDuplicate],
  );
  const [hiddenColumns, setHiddenColumns] = useState(
    columns.filter(({ hidden }) => !!hidden).map(({ name }) => name),
  );
  const handleToggleColumn = (name) => {
    setHiddenColumns(addOrRemove(hiddenColumns, name));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={t('DEM Templates')} />
          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <Row className="align-items-center justify-content-end m-auto">
                    <CountRowTablePlacement />
                    <Spinner
                      color="secondary"
                      animation="border"
                      hidden={!loading}
                    />
                    <CreateButton
                      label="New Template"
                      disabled={loading}
                      onPress={() => history.push('/demTemplates/new')}
                    />
                    <ToggleColumnButton
                      columns={columns}
                      hiddenColumns={hiddenColumns}
                      onToggleColumn={handleToggleColumn}
                    />
                    <ExportButtonPlacement />
                  </Row>
                  <DataTableNext
                    loading={loading}
                    rows={demTemplates}
                    columns={columns}
                    filters={filters}
                    onFiltersChange={setFilters}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    currentPage={currentPage}
                    onCurrentPageChange={setCurrentPage}
                    hiddenColumns={hiddenColumns}
                    onHiddenColumnsChange={setHiddenColumns}
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default List;
